// Pine Script Lite — public API

import { SERIES_HELPERS } from './builtins/ta'
import { lex } from './lexer/index'
import { parse, ParseError } from './parser/index'
import { transpile } from './transpiler/index'

export interface TranspileError {
  message: string
  line?: number
  col?: number
}

export interface PineCompileResult {
  /** Full JS ready to execute in the M16 worker scope */
  code: string
  /** Whether indicator should overlay on the main chart */
  overlay: boolean
  /** Indicator title from indicator() declaration */
  title: string
  /** Compilation errors, if any */
  errors: TranspileError[]
}

/**
 * Compile a Pine Script Lite source string into executable JS.
 * The output `code` is injected into the M16 Web Worker sandbox
 * which already has: open, high, low, close, volume, time, bars
 * and sma/ema/rsi/atr/highest/lowest/macd/plot/plotHistogram.
 */
export function compilePine(source: string): PineCompileResult {
  const errors: TranspileError[] = []

  // Lex
  let tokens
  try {
    tokens = lex(source)
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e)
    return { code: '', overlay: false, title: 'Indicator', errors: [{ message: msg }] }
  }

  // Parse
  let program
  try {
    program = parse(tokens)
  } catch (e) {
    if (e instanceof ParseError) {
      return {
        code: '',
        overlay: false,
        title: 'Indicator',
        errors: [{ message: e.message, line: e.line, col: e.col }],
      }
    }
    const msg = e instanceof Error ? e.message : String(e)
    return { code: '', overlay: false, title: 'Indicator', errors: [{ message: msg }] }
  }

  // Transpile
  const result = transpile(program)

  for (const err of result.errors) {
    errors.push({ message: err })
  }

  const fullCode = [SERIES_HELPERS, '', result.code].join('\n')

  return {
    code: fullCode,
    overlay: result.meta.overlay,
    title: result.meta.title || 'Indicator',
    errors,
  }
}
